/**
 * Which model the composer is actually using, and where that answer came from.
 *
 * Extracted from composerDraftStore so the resolution rule lives apart from the
 * zustand store that persists it. It is a pure function over three candidate
 * sources, which is exactly why it earns its own file: the bug it exists to
 * prevent is one source being added to the read without being added to the
 * guard.
 *
 * @module
 */
import type {
  ModelSelection,
  ProviderKind,
  ProviderModelOptions,
  ServerProvider,
} from "@cmmd-forge/contracts";
import type { UnifiedSettings } from "@cmmd-forge/contracts/settings";
import { belongsToDifferentProvider, normalizeModelSlug } from "@cmmd-forge/shared/model";

import { resolveAppModelSelection } from "./modelSelection";
import { getDefaultServerModel } from "./providerModels";

/**
 * Only the two draft fields this resolver reads. Declared here rather than
 * imported from the store so the pure rule does not depend on the module that
 * persists it.
 */
export interface ComposerDraftModelSources {
  readonly activeProvider: ProviderKind | null;
  readonly modelSelectionByProvider: Partial<Record<ProviderKind, ModelSelection>>;
}

export interface EffectiveComposerModelState {
  selectedModel: string;
  selectedProviderAccountId?: string;
  modelOptions: ProviderModelOptions | null;
}

function providerModelOptionsFromSelection(
  modelSelection: ModelSelection | null | undefined,
): ProviderModelOptions | null {
  if (!modelSelection?.options) {
    return null;
  }

  return {
    [modelSelection.provider]: modelSelection.options,
  };
}

function modelSelectionByProviderToOptions(
  map: Partial<Record<ProviderKind, ModelSelection>> | null | undefined,
): ProviderModelOptions | null {
  if (!map) return null;
  const result: Record<string, unknown> = {};
  for (const [provider, selection] of Object.entries(map)) {
    if (selection?.options) {
      result[provider] = selection.options;
    }
  }
  return Object.keys(result).length > 0 ? (result as ProviderModelOptions) : null;
}

/**
 * Which Provider Account the composer is targeting.
 *
 * Same precedence as the model itself: the draft's own pick first, then the
 * thread, then the project. A candidate only counts when its provider matches
 * the selected one, for the same reason the model guard exists: an account id
 * belongs to a provider, and carrying one across providers cannot authenticate.
 */
function resolveProviderAccountId(input: {
  readonly activeSelection: ModelSelection | undefined;
  readonly selectedProvider: ProviderKind;
  readonly candidates: ReadonlyArray<ModelSelection | null | undefined>;
}): string | undefined {
  if (input.activeSelection && "providerAccountId" in input.activeSelection) {
    return input.activeSelection.providerAccountId;
  }
  for (const candidate of input.candidates) {
    if (candidate?.provider === input.selectedProvider && "providerAccountId" in candidate) {
      return candidate.providerAccountId;
    }
  }
  return undefined;
}

export function deriveEffectiveComposerModelState(input: {
  draft: ComposerDraftModelSources | null | undefined;
  providers: ReadonlyArray<ServerProvider>;
  selectedProvider: ProviderKind;
  threadModelSelection: ModelSelection | null | undefined;
  projectModelSelection: ModelSelection | null | undefined;
  settings: UnifiedSettings;
}): EffectiveComposerModelState {
  // A stored thread/project modelSelection is only a valid source for `model`
  // when its OWN provider matches the currently selected provider. Model slugs
  // are provider-scoped namespaces (e.g. codex's "gpt-5.6-sol" vs apex's
  // "apex-flash") and normalizeModelSlug does not validate cross-provider
  // slugs — it only resolves aliases, and passes an unrecognized slug through
  // unchanged. Without this guard, switching the selected provider away from
  // a thread/project's stored provider (or a fresh runtime resetting the
  // project's default back to codex, e.g. recoverCloudProjects on a newly
  // provisioned cloud sprite) silently carries over a model slug that belongs
  // to the WRONG provider, producing an unusable pairing like
  // {provider: "apex", model: "gpt-5.6-sol"} that can never authenticate.
  // The provider check above is necessary but NOT sufficient, and that gap is
  // what shipped the bug this comment warns about. A stored pair whose provider
  // MATCHES can still carry a foreign model — live runtime p197 held
  // {"provider":"claudeAgent","model":"gpt-5.6-sol"} — which passes
  // `provider === selectedProvider` and then spawns `claude --model gpt-5.6-sol`.
  // Claude Code validates the model against account access, so it fails as
  // "Not logged in", sending everyone after credentials that were never broken.
  //
  // `belongsToDifferentProvider` is the same guard resolveModelSlug uses, so the
  // write path and this read path cannot disagree. Dropping a foreign model to
  // `undefined` heals rows persisted BEFORE that guard existed, rather than
  // requiring a migration.
  const acceptModelForProvider = (model: string | undefined): string | undefined =>
    model !== undefined && belongsToDifferentProvider(model, input.selectedProvider)
      ? undefined
      : model;
  const threadModel = acceptModelForProvider(
    input.threadModelSelection?.provider === input.selectedProvider
      ? input.threadModelSelection.model
      : undefined,
  );
  const projectModel = acceptModelForProvider(
    input.projectModelSelection?.provider === input.selectedProvider
      ? input.projectModelSelection.model
      : undefined,
  );
  const baseModel =
    normalizeModelSlug(threadModel ?? projectModel, input.selectedProvider) ??
    getDefaultServerModel(input.providers, input.selectedProvider);
  const activeSelection = input.draft?.modelSelectionByProvider?.[input.selectedProvider];
  const selectedProviderAccountId = resolveProviderAccountId({
    activeSelection,
    selectedProvider: input.selectedProvider,
    candidates: [input.threadModelSelection, input.projectModelSelection],
  });
  // The draft bucket gets the SAME guard as thread and project above. It did
  // not, and that was the one hole in the comment's claim that "the write path
  // and this read path cannot disagree": a bucket keyed by provider can still
  // hold a foreign slug, and this read handed it straight to
  // resolveAppModelSelection. The shim that pinned selectedProvider to codex
  // hid it, because the read then always hit the codex bucket.
  const draftModel = acceptModelForProvider(activeSelection?.model);
  const selectedModel = draftModel
    ? resolveAppModelSelection(input.selectedProvider, input.settings, input.providers, draftModel)
    : baseModel;
  const modelOptions =
    modelSelectionByProviderToOptions(input.draft?.modelSelectionByProvider) ??
    providerModelOptionsFromSelection(input.threadModelSelection) ??
    providerModelOptionsFromSelection(input.projectModelSelection) ??
    null;

  return {
    selectedModel,
    ...(selectedProviderAccountId ? { selectedProviderAccountId } : {}),
    modelOptions,
  };
}
